/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';
import {
  dp,
  maxPressure,
  maxTemperature,
  minPressure,
  minTemperature,
  minEmagramTemperature,
  staticPressureArray,
} from './constants';
import type { Margin } from './drawUtils';
import {
  initializeSvg,
  createScales,
  drawAxes,
  SOUNDING_COLORS,
  plotSounding,
} from './drawUtils';
import { useSharedMouseY } from './SharedMouseYProvider';
import type {
  SoundingFeature,
  SoundingDiagramType,
} from '../../api/edr-api/types';
import {
  constructTooltipData,
  drawSoundingsTooltip,
} from '../SoundingsTooltip/utils';
import { drawStaticLines } from '../StaticLines/utils';

const shadow =
  '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 4px 0 rgba(0, 0, 0, 0.12), 0 2px 4px 0 rgba(0, 0, 0, 0.14)';

const soundingsStyle = {
  tooltip: {
    display: 'block',
    position: 'absolute',
    borderRadius: '1.5px',
    backgroundColor: 'geowebColors.soundings.tooltip.background',
    boxShadow: shadow,
    padding: '2px 4px',
    pointerEvents: 'none',
    zIndex: 500,
  },
  pressure: {
    fontSize: '10px',
  },
  temperature: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  dewPoint: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  wind: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
};

export interface SoundingProps {
  d3GraphId: string;
  data: SoundingFeature[][];
  svgDimensions: {
    width: number;
    height: number;
    margin: Margin;
  };
  showTooltip?: boolean;
  diagramType?: SoundingDiagramType;
}

export const Soundings: React.FC<SoundingProps> = ({
  d3GraphId,
  data,
  svgDimensions,
  showTooltip = true,
  diagramType = 'SKEWT',
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const pressureRef = useRef<HTMLDivElement | null>(null);
  const tempRef = useRef<HTMLDivElement | null>(null);
  const dewpointRef = useRef<HTMLDivElement | null>(null);

  // Refs to store current scale/data for shared mouse effect
  const yScaleRef = useRef<d3.ScaleLinear<number, number> | null>(null);
  const marginRef = useRef<Margin | null>(null);
  const widthRef = useRef<number>(0);
  const svgRectRef = useRef<DOMRect | null>(null);
  const tooltipDataRef = useRef<ReturnType<typeof constructTooltipData> | null>(
    null,
  );

  const { clientY } = useSharedMouseY();

  // Set SVG dimensions (required props; no optional chaining needed)
  const { width: baseWidth, height: baseHeight, margin } = svgDimensions;
  const width = baseWidth - (margin.left + margin.right);
  const height = baseHeight - (margin.top + margin.bottom);

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }

    const svg = initializeSvg({
      svgRef,
      width,
      height,
      margin,
      diagramType,
      d3GraphId,
    }) as unknown as d3.Selection<SVGElement, unknown, HTMLElement, unknown>;

    const svgRectangle = svgRef.current.getBoundingClientRect();
    svgRectRef.current = svgRectangle;
    marginRef.current = margin;
    widthRef.current = width;

    const minT =
      diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;
    const maxT = maxTemperature;

    const [xScale, yScale] = createScales(
      diagramType,
      width,
      height,
      minPressure,
      maxPressure,
      minT,
      maxT,
    );
    yScaleRef.current = yScale;

    drawAxes({ svg, xScale, yScale, height, diagramType });

    drawStaticLines({
      diagramType,
      dp,
      staticPressureArray,
      minTemperature: minT,
      maxTemperature: maxT,
      height,
      width,
      xScale,
      yScale,
      minPressure,
      maxPressure,
      svg,
      d3GraphId,
    });

    data.forEach((features, index) => {
      const color = SOUNDING_COLORS[index] || SOUNDING_COLORS[0];
      const temperatureValues = features.map(
        (feature) => feature.properties.temp,
      );
      const pressureValues = features.map(
        (feature) => feature.properties.pressure,
      );
      plotSounding({
        temperatureValues,
        pressureValues,
        color,
        lineStyle: 'none',
        xScale,
        yScale,
        height,
        svg,
        diagramType,
      });
      const dewpointValues = features.map(
        (feature) => feature.properties.dewpoint,
      );
      plotSounding({
        temperatureValues: dewpointValues,
        pressureValues,
        color,
        lineStyle: '8, 2',
        xScale,
        yScale,
        height,
        svg,
        diagramType,
      });
    });

    tooltipDataRef.current = constructTooltipData(data);

    const svgRefCur = svgRef.current;
    // eslint-disable-next-line consistent-return, @typescript-eslint/explicit-function-return-type
    return () => {
      d3.select(svgRefCur).selectAll('*').remove();
    };
  }, [data, diagramType, width, height, d3GraphId, margin]);

  useEffect(() => {
    if (!showTooltip || !svgRef.current) {
      pressureRef.current && (pressureRef.current.style.display = 'none');
      tempRef.current && (tempRef.current.style.display = 'none');
      dewpointRef.current && (dewpointRef.current.style.display = 'none');
      return;
    }

    const rect = svgRectRef.current ?? svgRef.current.getBoundingClientRect();
    if (clientY == null || clientY < rect.top || clientY > rect.bottom) {
      pressureRef.current && (pressureRef.current.style.display = 'none');
      tempRef.current && (tempRef.current.style.display = 'none');
      dewpointRef.current && (dewpointRef.current.style.display = 'none');
      return;
    }

    if (!yScaleRef.current || !marginRef.current || !tooltipDataRef.current) {
      return;
    }

    // Synthetic event with shared clientY
    const synthetic = {
      clientX: rect.left + rect.width / 2,
      clientY,
      pageY: clientY + window.scrollY,
      target: svgRef.current,
      currentTarget: svgRef.current,
    } as unknown as MouseEvent;

    const pressureRefd3 = d3.select(pressureRef.current);
    const tempRefd3 = d3.select(tempRef.current);
    const dewpointRefd3 = d3.select(dewpointRef.current);

    const minT =
      diagramType === 'EMAGRAM' ? minEmagramTemperature : minTemperature;
    const maxT = maxTemperature;
    const [, yScale] = createScales(
      diagramType,
      width,
      height,
      minPressure,
      maxPressure,
      minT,
      maxT,
    );

    drawSoundingsTooltip({
      event: synthetic,
      yScale,
      svgRectangle: rect,
      margin: marginRef.current,
      width: widthRef.current,
      pressureRefd3,
      tempRefd3,
      dewpointRefd3,
      tooltipData: tooltipDataRef.current,
      diagramType,
    });
  }, [clientY, showTooltip, diagramType, data, width, height]);

  return (
    <Box ref={canvasRef} sx={{ position: 'relative' }}>
      <svg ref={svgRef} />
      <Box
        data-testid="pressure-tooltip"
        ref={pressureRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.pressure,
        }}
      />
      <Box
        data-testid="temperature-tooltip"
        ref={tempRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.temperature,
        }}
      />
      <Box
        data-testid="dewpoint-tooltip"
        ref={dewpointRef}
        sx={{
          ...soundingsStyle.tooltip,
          ...soundingsStyle.dewPoint,
        }}
      />
    </Box>
  );
};
